import { useEffect, useRef, useState } from 'react';
import { Maximize2, Minus, Plus, RotateCcw, X } from 'lucide-react';
import mermaid from 'mermaid';

// Initialise once — light theme, no startOnLoad (we render manually).
mermaid.initialize({
  startOnLoad: false,
  theme: 'default',
  securityLevel: 'strict',
  fontFamily: 'ui-sans-serif, system-ui, sans-serif',
});

let idCounter = 0;

interface MermaidDiagramProps {
  chart: string;
  /** Show a maximize button that opens the diagram in a fullscreen
   *  overlay with zoom controls. Opt-in so existing usages keep their
   *  plain inline rendering. */
  zoomable?: boolean;
  /** Title shown in the fullscreen overlay header. */
  title?: string;
  /** Diagram text size in px via a themeVariables init directive — sizes
   *  both the measured node boxes and the displayed text, so boxes fit
   *  their sentences. Safe only for charts whose long labels carry explicit
   *  <br/> wrapping (the RCA backend emits it); unwrapped long labels
   *  render nowrap at this size and clip mid-word (the 1.73.26 bug). */
  textSize?: number;
}

const ZOOM_MIN = 0.5;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.25;

export function MermaidDiagram({ chart, zoomable = false, title, textSize }: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [svg, setSvg] = useState<string>('');
  const [expanded, setExpanded] = useState(false);
  const [zoom, setZoom] = useState(1.5); // fullscreen opens pre-zoomed for readability

  useEffect(() => {
    let cancelled = false;
    const id = `mermaid-${++idCounter}`;

    (async () => {
      try {
        // The directive alone sets BOTH measurement and display (verified
        // headless: label 11px, box shrinks to fit, max-width intact). Never
        // inject a style attribute into the <svg> tag: it duplicates and
        // thereby drops mermaid's own style="max-width:…", and the width=100%
        // svg then scales up to the container — text looks unchanged.
        // wrappingWidth must exceed the widest pre-wrapped label line or
        // mermaid clips it at that cap (default 200px — the right-edge
        // truncation bug); padding 2 = minimum box chrome around the text.
        const src = textSize
          ? `%%{init: {"themeVariables": {"fontSize": "${textSize}px"}, "flowchart": {"padding": 2, "wrappingWidth": 400}}}%%\n${chart.trim()}`
          : chart.trim();
        const { svg: rendered } = await mermaid.render(id, src);
        if (!cancelled) {
          setSvg(rendered);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to render diagram');
          setSvg('');
        }
        // Clean up the broken element mermaid may have injected
        document.getElementById(`d${id}`)?.remove();
      }
    })();

    return () => { cancelled = true; };
  }, [chart, textSize]);

  // Fullscreen: close on Escape, lock body scroll while open.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setExpanded(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [expanded]);

  if (error) {
    return (
      <div className="my-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
        <p className="font-medium mb-1">Diagram could not be rendered</p>
        <p className="text-xs mb-1.5 text-red-600">
          The diagram data is invalid. Re-running Analyze on the ticket regenerates
          it; if the error persists, report the ticket key.
        </p>
        <pre className="text-[10px] whitespace-pre-wrap text-red-400">{error}</pre>
      </div>
    );
  }

  return (
    <>
      <div className="relative group">
        {zoomable && svg && (
          <button
            type="button"
            onClick={() => { setZoom(1.5); setExpanded(true); }}
            title="Maximize diagram"
            className="absolute top-1 right-1 z-10 p-1.5 rounded-md bg-white/90 border border-gray-200
                       text-gray-500 hover:text-gray-800 hover:border-gray-300 shadow-sm
                       opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <Maximize2 size={14} />
          </button>
        )}
        <div
          ref={containerRef}
          className={`my-3 flex justify-center overflow-x-auto${zoomable && svg ? ' cursor-zoom-in' : ''}`}
          onClick={zoomable && svg ? () => { setZoom(1.5); setExpanded(true); } : undefined}
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>

      {expanded && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full h-full flex flex-col overflow-hidden">
            {/* Header: title + zoom controls */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200 bg-gray-50">
              <span className="text-sm font-semibold text-gray-800">
                {title || 'Diagram'}
              </span>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
                  title="Zoom out"
                  className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100"
                >
                  <Minus size={14} />
                </button>
                <span className="text-xs text-gray-600 w-12 text-center tabular-nums">
                  {Math.round(zoom * 100)}%
                </span>
                <button
                  type="button"
                  onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
                  title="Zoom in"
                  className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100"
                >
                  <Plus size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setZoom(1.5)}
                  title="Reset zoom"
                  className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 ml-1"
                >
                  <RotateCcw size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setExpanded(false)}
                  title="Close (Esc)"
                  className="p-1.5 rounded-md border border-gray-200 text-gray-600 hover:bg-red-50 hover:text-red-600 ml-3"
                >
                  <X size={14} />
                </button>
              </div>
            </div>
            {/* Diagram area — CSS zoom keeps layout size in sync so
                scrollbars pan the scaled content naturally. */}
            <div className="flex-1 overflow-auto p-6">
              <div
                style={{ zoom }}
                className="flex justify-center min-w-fit"
                dangerouslySetInnerHTML={{ __html: svg }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}
